import React, { useMemo } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
  GridRowsProp,
} from '@mui/x-data-grid';
import { PaginationType } from './Groups';
import { colors } from '../utils/constants';

const UserAnalyticsTable = ({
  rows = [],
  columns = [],
  onRowClick = () => {},
  sx = {},
  setPaginationModel = () => {},
  paginationModel = { page: 0, pageSize: 5 },
  totalRowsCount = 0,
}: {
  rows: GridRowsProp;
  columns: GridColDef[];
  pageSize?: number;
  onRowClick?: (params: GridRowParams) => void;
  sx?: SxProps<Theme>;
  setPaginationModel?: (model: PaginationType) => void;
  paginationModel?: PaginationType;
  totalRowsCount?: number;
}) => {
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  // Preprocess rows to ensure each has a unique row ID
  const processedRows = useMemo(
    () =>
      rows &&
      rows?.map((row, index) => ({
        ...row,
        _uniqueRowId: `${row.id}-${index}`,
      })),
    [rows]
  );

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      <DataGrid
        rows={processedRows}
        columns={columns}
        getRowId={(row) => row._uniqueRowId}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        localeText={{ noRowsLabel: `No results found` }}
        rowCount={totalRowsCount}
        onRowClick={onRowClick}
        disableRowSelectionOnClick
        disableColumnResize
        disableAutosize
        sx={dataGridSx}
      />
    </Box>
  );
};

export default UserAnalyticsTable;

const dataGridSx: SxProps = {
  fontSize: '14px',
  border: '1px solid #e3e3e3',
  borderRadius: '8px',
  backgroundColor: '#fff',
  '& .MuiDataGrid-columnHeaders': {
    color: '#64748B',
    fontWeight: 500,
    borderBottom: '1px solid #F1F5F9',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: '#FBFBFB',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 500,
    textAlign: 'center',
    width: '100%',
    fontSize: '16px',
    color: colors.black,
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f2f2f2',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#F1F5F9',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: 'none',
  },
  '& .MuiDataGrid-virtualScroller': {
    marginTop: '4px',
  },
  '& .centerCell': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
  },
  '& .recipientTypeBadge': {
    fontSize: '11px',
    fontWeight: 400,
    color: '#444',
    backgroundColor: '#f1f1f1',
    borderRadius: '10px',
    padding: '2px 8px',
    marginLeft: '8px',
    textTransform: 'lowercase',
  },
  '& .MuiDataGrid-columnHeaders div[role="row"]': {
    backgroundColor: ' #FBFBFB',
  },
};
